"use client";

// Using simple button with custom styling
import { clientLogger } from "@/lib/logger";
import { cn } from "@/lib/utils";
import { useAppData } from "@/store/appStore";
import { useEffect } from "react";
import { useConfig } from "@/hooks/useConfig";

interface ToggleProps {
	tabs: ("devices" | "cars" | "smartphones")[];
	page: string;
	initialTab?: "devices" | "cars" | "smartphones";
	bare?: boolean;
}

const Toggle = ({
	tabs,
	page,
	initialTab = "cars",
	bare = false,
}: ToggleProps) => {
	const [appData, setData, , hasHydrated] = useAppData();
	// Call useConfig for its auth/user-change refetch effect.
	useConfig();

	// Define consistent text styles for toggle buttons
	const toggleTextStyle = "font-light text-sm";

	const showToggle = appData?.showToggle === true;

	// When toggle is hidden, set onboardingCompletionType based on which product type is enabled
	useEffect(() => {
		if (!hasHydrated) return;

		if (!showToggle) {
			if (appData?.showCar && !appData?.showDevice) {
				setData({ onboardingCompletionType: "cars" });
			} else if (appData?.showDevice && !appData?.showCar) {
				setData({ onboardingCompletionType: "devices" });
			}
		}
	}, [showToggle, appData?.showCar, appData?.showDevice, setData, hasHydrated]);

	// Initialize onboardingCompletionType if missing (only once on mount)
	useEffect(() => {
		if (!appData?.onboardingCompletionType) {
			setData({
				onboardingCompletionType: initialTab === "cars" ? "cars" : "devices",
			});
		}
	}, []); // Only run on mount

	// If toggle should not be shown, return null
	if (!showToggle) {
		return null;
	}

	const handleCarTabClick = () => {
		if (page === "product-list") {
			setData({
				onboardingCompletionType: "cars",
				selectedFilters: {},
				selectedCategories: ["all"],
				filteredProductList: [],
			});
		} else {
			setData({ onboardingCompletionType: "cars" });
		}

		clientLogger.info("Tab switched to cars", {
			component: "Toggle",
			event: "tab_switch",
			newTab: tabs[0],
		});
	};

	const handleDeviceTabClick = () => {
		if (page === "product-list") {
			setData({
				onboardingCompletionType: "devices",
				selectedFilters: {},
				selectedCategories: ["mobile"],
				filteredProductList: [],
			});
		} else {
			setData({ onboardingCompletionType: "devices" });
		}

		clientLogger.info("Tab switched to devices", {
			component: "Toggle",
			event: "tab_switch",
			newTab: "devices",
		});
	};

	const buttons = (
		<>
			<button
				id="homepage_toggle_device"
				type="button"
				onClick={handleDeviceTabClick}
				className={cn(
					"flex justify-center items-center h-[36px] md:h-auto px-3 py-1.5 md:px-[20px] md:py-[12px] gap-2 outline-none border-none rounded-[10px] transition-colors duration-200",
					toggleTextStyle,
					appData?.onboardingCompletionType === "devices"
						? "bg-automint-black text-automint-neon"
						: "bg-transparent text-header-gray hover:bg-gray-700/50",
				)}
			>
				{tabs[1] === "devices" ? "Devices" : "Smartphones"}
			</button>
			<button
				id="homepage_toggle_car"
				type="button"
				onClick={handleCarTabClick}
				className={cn(
					"flex justify-center items-center h-[36px] md:h-auto px-3 py-1.5 md:px-[20px] md:py-[12px] gap-2 outline-none border-none rounded-[10px] transition-colors duration-200",
					toggleTextStyle,
					appData?.onboardingCompletionType === "cars"
						? "bg-automint-black text-automint-neon"
						: "bg-transparent text-header-gray hover:bg-gray-700/50",
				)}
			>
				{tabs[0] === "cars" && "Cars"}
			</button>
		</>
	);

	// bare=true: caller owns the container (used in the header pill)
	if (bare) return buttons;

	return (
		<div className="flex justify-center ios-safe-area-top">
			<div className="inline-flex items-center gap-2 p-1 rounded-[10px] bg-neutral-800">
				{buttons}
			</div>
		</div>
	);
};

// Add display name for better debugging
Toggle.displayName = "Toggle";

export default Toggle;
